import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { DateTime } from "luxon";
import Iconify from "src/components/iconify/iconify";
import { Rental, RentalTrailerEvent } from "src/models";
import axios, { endpoints, formatEndpoint } from "src/utils/axios";
import { Constants } from "src/constants";

type RentalDocument = {
  name: string;
  url: string;
  at?: string | null;
};

type DocumentGroup = {
  title: string;
  documents: RentalDocument[];
};

const IMAGE_EXTENSIONS = /\.(png|jpe?g|gif|webp|heic)(\?.*)?$/i;

function isImage(url: string) {
  return IMAGE_EXTENSIONS.test(url) || url.startsWith("data:image/");
}

function withUrl(name: string, url: string | null | undefined, at?: string | null): RentalDocument[] {
  return url ? [{ name, url, at }] : [];
}

function numbered(prefix: string, urls: string[] | null | undefined, at?: string | null): RentalDocument[] {
  return (urls || []).map((url, index) => ({ name: `${prefix} ${index + 1}`, url, at }));
}

function inspectionDocuments(event: RentalTrailerEvent, unitLabel: string): RentalDocument[] {
  const isReturn = event.rentalTrailerEvent === Constants.RENTAL_TRAILER_EVENT_TYPE_IN;
  const formName = isReturn ? "Return Inspection Form" : `Outbound ${unitLabel} Inspection Form`;
  // the return form has no sent-then-signed pair: it is produced once on completion
  const forms = isReturn
    ? withUrl(formName, event.signedContractPdfUrl, event.confirmedAt)
    : [
        ...withUrl(formName, event.appliedContractInfo?.contractPdfUrl, event.contractSentAt || event.eventTime),
        ...withUrl(`Signed ${formName}`, event.signedContractPdfUrl, event.confirmedAt),
      ];
  return [
    ...forms,
    ...numbered("Customer ID", event.customerIdPhotos, event.confirmedAt),
    ...numbered(isReturn ? "Return photo" : "Outbound photo", isReturn ? event.returnPhotos : event.photos, event.eventTime),
  ];
}

function rentalGroups(rental: Rental, events: RentalTrailerEvent[], unitLabel: string): DocumentGroup[] {
  const insurance: RentalDocument[] = [
    ...withUrl("Insurance Request", rental.insurancePdfUrl, rental.insuranceSentAt),
    ...withUrl("PD Form", rental.pdFile, rental.insuranceSentAt),
    ...(rental.insuranceAttachments || []).flatMap((attachment) => [
      ...withUrl(`${attachment.name || "Form"} (blank)`, attachment.blankUrl, rental.insuranceSentAt),
      ...withUrl(`${attachment.name || "Form"} (completed)`, attachment.returnedUrl, attachment.returnedAt),
    ]),
    ...numbered("Customer insurance document", rental.customerInsuranceDocuments, rental.insuranceSubmittedAt),
  ];

  const agreement: RentalDocument[] = [
    ...withUrl("Agreement", rental.agreementContractInfo?.contractPdfUrl, rental.agreementSentAt),
    ...withUrl("Signed Agreement", rental.signedAgreementPdfUrl, rental.agreementConfirmedAt),
    ...withUrl("Customer Signature", rental.agreementSignature, rental.agreementSubmittedAt),
    ...numbered("Customer ID", rental.agreementIdPhotos, rental.agreementSubmittedAt),
  ];

  const outEvent = events.find((event) => event.id === rental.outEventId);
  const inEvent = events.find((event) => event.id === rental.inEventId);
  const otherEvents = events
    .filter((event) => event.id !== rental.outEventId && event.id !== rental.inEventId)
    .sort((a, b) => (a.eventTime < b.eventTime ? 1 : -1));

  return [
    { title: "Insurance", documents: insurance },
    { title: "Agreement", documents: agreement },
    { title: "Outbound Inspection", documents: outEvent ? inspectionDocuments(outEvent, unitLabel) : [] },
    { title: "Return Inspection", documents: inEvent ? inspectionDocuments(inEvent, unitLabel) : [] },
    {
      title: `Earlier inspections on this ${unitLabel.toLowerCase()}`,
      documents: otherEvents.flatMap((event) => inspectionDocuments(event, unitLabel)),
    },
  ].filter((group) => group.documents.length > 0);
}

type Props = {
  rental: Rental;
  unitLabel: string;
  timezone: string;
};

export function RentalDocuments({ rental, unitLabel, timezone }: Props) {
  const [events, setEvents] = useState<RentalTrailerEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const { trailerId } = rental;

  useEffect(() => {
    if (!trailerId) {
      setEvents([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    axios
      .get(formatEndpoint(endpoints.rentalTrailerEvents.byTrailerId, { trailerId }))
      .then((response) => {
        if (!cancelled) {
          setEvents(response.data || []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setEvents([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [trailerId]);

  const groups = rentalGroups(rental, events, unitLabel);
  const formatDate = (iso?: string | null) =>
    iso ? DateTime.fromISO(iso).setZone(timezone).toFormat("MM/dd/yyyy HH:mm") : "";

  if (loading && groups.length === 0) {
    return (
      <Stack alignItems="center" py={4}>
        <CircularProgress size={24} />
      </Stack>
    );
  }

  if (groups.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" py={2}>
        No documents yet. They appear here as the insurance request, agreement and inspections are
        sent and signed.
      </Typography>
    );
  }

  return (
    <Stack gap={3} py={1}>
      {groups.map((group) => (
        <Stack key={group.title} gap={1}>
          <Typography variant="subtitle2">{group.title}</Typography>
          {group.documents.map((document, index) => (
            <Stack
              key={`${document.url}-${index}`}
              direction="row"
              alignItems="center"
              gap={1.5}
              sx={{ px: 1.5, py: 1, border: "1px solid", borderColor: "divider", borderRadius: 1 }}
            >
              {isImage(document.url) ? (
                <Box
                  component="img"
                  src={document.url}
                  sx={{ width: 40, height: 40, objectFit: "cover", borderRadius: 0.5, flexShrink: 0 }}
                />
              ) : (
                <Iconify icon="solar:document-text-bold" width={28} sx={{ color: "text.secondary", flexShrink: 0 }} />
              )}
              <Box flex={1} minWidth={0}>
                <Typography variant="body2" noWrap>{document.name}</Typography>
                {document.at && (
                  <Typography variant="caption" color="text.secondary">{formatDate(document.at)}</Typography>
                )}
              </Box>
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                href={document.url}
                target="_blank"
                rel="noopener"
              >
                Open
              </Button>
            </Stack>
          ))}
        </Stack>
      ))}
    </Stack>
  );
}
